if (!customElements.get('pack-builder')) {
  class PackBuilder extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;

      this._initialized = true;
      this.selection = new Map();
      this.activeFilter = 'all';
      this.baseCartTotal = Number(this.dataset.progressCartTotal || 0);
      this.moneyFormat =
        (window.KROWN && KROWN.settings && KROWN.settings.shop_money_format) ||
        this.dataset.moneyFormat ||
        '{{amount_with_comma_separator}} €';
      this.isAdding = false;
      this.mountMobileSticky();

      this.restoreSelection();
      this.prepareProducts(this);
      this.pruneSelection();

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));

      this.applyFilter();
      this.render();
    }

    // Le footer est déplacé dans body pour éviter les contextes d'empilement
    // du thème. Ses événements restent explicitement liés à ce pack-builder.
    mountMobileSticky() {
      const sticky = this.querySelector('[data-pack-mobile-sticky]');
      if (!sticky) return;
      this.mobileSticky = sticky;
      document.body.appendChild(sticky);
      sticky.addEventListener('click', event => {
        const submit = event.target.closest('[data-pack-submit]');
        if (submit) {
          event.preventDefault();
          if (!submit.disabled) this.addPackToCart();
          return;
        }
        const toggle = event.target.closest('[data-pack-mobile-view-products]');
        if (toggle) {
          event.preventDefault();
          const list = sticky.querySelector('[data-pack-mobile-selected]');
          if (list) {
            list.hidden = !list.hidden;
            toggle.setAttribute('aria-expanded', String(!list.hidden));
          }
          return;
        }
        const remove = event.target.closest('[data-pack-remove]');
        if (remove) {
          event.preventDefault();
          this.removeProduct(String(remove.dataset.packRemove || ''));
        }
      });
    }

    disconnectedCallback() {
      // En édition Shopify, l'ancienne section peut être retirée puis recréée.
      if (this.mobileSticky) {
        this.mobileSticky.remove();
        this.mobileSticky = null;
      }
    }

    allPackElements(selector) {
      const elements = Array.from(this.querySelectorAll(selector));
      if (this.mobileSticky) elements.push(...this.mobileSticky.querySelectorAll(selector));
      return elements;
    }

    renderMobileRewards(packTotal, itemCount) {
      const sticky = this.mobileSticky;
      if (!sticky) return;
      sticky.classList.toggle('is-visible', itemCount > 0);
      const price = sticky.querySelector('[data-pack-mobile-price]');
      const count = sticky.querySelector('[data-pack-mobile-count]');
      if (price) price.textContent = this.formatMoney(packTotal);
      if (count) count.textContent = String(itemCount);
      const panel = sticky.querySelector('[data-pack-mobile-rewards]');
      if (!panel) return;
      // Les paliers du sticky concernent uniquement le pack en cours,
      // jamais les articles déjà présents dans le panier Shopify.
      const total = packTotal;
      const thresholds = [1, 2, 3].map(index => {
        const raw = panel.getAttribute(`data-threshold-${index}`);
        const value = Number(raw);
        return Number.isFinite(value) && value > 0 ? value : 0;
      });
      const labels = ['la livraison offerte', '10 % de remise', '20 % de remise'];
      const message = sticky.querySelector('[data-pack-mobile-message]');
      const configured = thresholds
        .map((amount, index) => ({ amount, index }))
        .filter(tier => tier.amount > 0)
        .sort((a, b) => a.amount - b.amount);
      const next = configured.find(tier => total < tier.amount);

      if (message) {
        if (!configured.length) {
          message.textContent = 'Les paliers ne sont pas configurés dans les paramètres du thème.';
        } else if (next) {
          message.textContent = `Plus que ${this.formatMoney(next.amount - total)} pour profiter de ${labels[next.index]}.`;
        } else {
          message.textContent = 'Vous profitez du dernier palier de remise !';
        }
      }

      const max = configured.length ? Math.max(...configured.map(tier => tier.amount)) : 0;
      const progress = sticky.querySelector('[data-pack-mobile-progress]');
      if (progress) progress.style.width = `${max > 0 ? Math.max(0, Math.min(100, total / max * 100)) : 0}%`;

      // Positionnement proportionnel des points sur la barre, selon les vrais seuils.
      const track = panel.querySelector('.mbp__mobile-reward-track');
      if (track) {
        let markers = track.querySelector('.mbp__mobile-reward-markers');
        if (!markers) {
          markers = document.createElement('div');
          markers.className = 'mbp__mobile-reward-markers';
          markers.setAttribute('aria-hidden', 'true');
          track.appendChild(markers);
        }
        markers.replaceChildren();
        thresholds.forEach((amount, index) => {
          if (amount <= 0 || max <= 0) return;
          const marker = document.createElement('span');
          marker.className = 'mbp__mobile-reward-marker';
          marker.dataset.tier = String(index + 1);
          marker.style.left = `${Math.min(100, amount / max * 100)}%`;
          marker.classList.toggle('is-achieved', total >= amount);
          markers.appendChild(marker);
        });
      }
      panel.querySelectorAll('.mbp__mobile-reward-labels > div').forEach((element, index) => {
        element.classList.toggle('is-achieved', thresholds[index] > 0 && total >= thresholds[index]);
      });
    }

    prepareProducts(scope) {
      scope.querySelectorAll('[data-pack-product]').forEach(productElement => {
        if (productElement.dataset.packReady === 'true') return;

        const control = productElement.querySelector('[data-pack-control]');
        const cardTextElements = productElement.querySelectorAll('.product-item__text');
        const cardText = cardTextElements[cardTextElements.length - 1];

        if (control && cardText && control.parentElement !== cardText) {
          cardText.append(control);
        }

        productElement.dataset.packReady = 'true';
        this.hydrateProduct(productElement);
      });
    }

    hydrateProduct(productElement) {
      const control = productElement.querySelector('[data-pack-control]');
      if (!control) return;

      const productId = String(control.dataset.productId || '');
      const selectedItem = this.selection.get(productId);
      const variantSelect = control.querySelector('[data-pack-variant-select]');

      if (selectedItem && variantSelect) {
        const storedOption = Array.from(variantSelect.options).find(
          option => String(option.value) === String(selectedItem.variantId)
        );

        if (storedOption) {
          variantSelect.value = String(selectedItem.variantId);
          const refreshedItem = this.getSelectedItem(control, selectedItem.quantity);
          if (refreshedItem) this.selection.set(productId, refreshedItem);
        } else {
          this.selection.delete(productId);
        }
      }

      this.syncProduct(productId);
    }

    pruneSelection() {
      const availableProductIds = new Set(
        Array.from(this.querySelectorAll('[data-pack-product]')).map(productElement =>
          String(productElement.dataset.productId || '')
        )
      );
      let selectionChanged = false;

      Array.from(this.selection.keys()).forEach(productId => {
        if (availableProductIds.has(productId)) return;
        this.selection.delete(productId);
        selectionChanged = true;
      });

      if (selectionChanged) this.persistSelection();
    }

    onClick(event) {
      const filterButton = event.target.closest('[data-pack-filter]');
      if (filterButton && this.contains(filterButton)) {
        event.preventDefault();
        this.setFilter(filterButton.dataset.packFilter || 'all');
        return;
      }

      const increaseButton = event.target.closest('[data-pack-increase]');
      if (increaseButton && this.contains(increaseButton)) {
        event.preventDefault();
        this.changeQuantity(increaseButton.closest('[data-pack-control]'), 1);
        return;
      }

      const decreaseButton = event.target.closest('[data-pack-decrease]');
      if (decreaseButton && this.contains(decreaseButton)) {
        event.preventDefault();
        this.changeQuantity(decreaseButton.closest('[data-pack-control]'), -1);
        return;
      }

      const removeButton = event.target.closest('[data-pack-remove]');
      if (removeButton && this.contains(removeButton)) {
        event.preventDefault();
        this.removeProduct(String(removeButton.dataset.packRemove || ''));
        return;
      }

      const submitButton = event.target.closest('[data-pack-submit]');
      if (submitButton && this.contains(submitButton)) {
        event.preventDefault();
        this.addPackToCart();
        return;
      }

    }

    onChange(event) {
      const variantSelect = event.target.closest('[data-pack-variant-select]');
      if (!variantSelect || !this.contains(variantSelect)) return;

      const control = variantSelect.closest('[data-pack-control]');
      const productId = String(control.dataset.productId || '');
      const existingItem = this.selection.get(productId);

      if (existingItem && existingItem.quantity > 0) {
        const updatedItem = this.getSelectedItem(control, existingItem.quantity);
        if (updatedItem) this.selection.set(productId, updatedItem);
      }

      this.syncProduct(productId);
      this.persistSelection();
      this.render();
    }

    setFilter(filterId) {
      this.activeFilter = filterId;

      this.querySelectorAll('[data-pack-filter]').forEach(button => {
        const isActive = button.dataset.packFilter === filterId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      this.applyFilter();
    }

    applyFilter() {
      let visibleProducts = 0;

      this.querySelectorAll('[data-pack-product]').forEach(productElement => {
        const filterIds = String(productElement.dataset.filterIds || '')
          .split(',')
          .filter(Boolean);
        const isVisible =
          this.activeFilter === 'all' || filterIds.includes(this.activeFilter);

        productElement.hidden = !isVisible;
        if (isVisible) visibleProducts += 1;
      });

      let emptyMessage = this.querySelector('[data-pack-filter-empty]');
      const productsGrid = this.querySelector('[data-pack-products]');

      if (visibleProducts === 0 && productsGrid) {
        if (!emptyMessage) {
          emptyMessage = document.createElement('p');
          emptyMessage.className = 'no-content-message mbp__filter-empty';
          emptyMessage.setAttribute('data-pack-filter-empty', '');
          productsGrid.insertAdjacentElement('afterend', emptyMessage);
        }
        emptyMessage.textContent = this.dataset.noResultsLabel || 'Aucun produit dans cette thématique.';
        emptyMessage.hidden = false;
      } else if (emptyMessage) {
        emptyMessage.hidden = true;
      }
    }

    changeQuantity(control, delta) {
      if (!control) return;

      const productId = String(control.dataset.productId || '');
      const currentItem = this.selection.get(productId);
      const selectedItem = this.getSelectedItem(control, currentItem ? currentItem.quantity : 0);
      if (!selectedItem) return;

      const nextQuantity = Math.max(
        0,
        Math.min(selectedItem.max, selectedItem.quantity + delta)
      );

      if (nextQuantity === 0) {
        this.selection.delete(productId);
      } else {
        selectedItem.quantity = nextQuantity;
        this.selection.set(productId, selectedItem);
      }

      this.syncProduct(productId);
      this.persistSelection();
      this.render();
    }

    removeProduct(productId) {
      if (!productId) return;

      this.selection.delete(productId);
      this.syncProduct(productId);
      this.persistSelection();
      this.render();
    }

    getSelectedItem(control, quantity) {
      const variantSelect = control.querySelector('[data-pack-variant-select]');
      const option = variantSelect && variantSelect.selectedOptions
        ? variantSelect.selectedOptions[0]
        : null;

      if (!option) return null;

      const maximum = Math.max(0, Number(option.dataset.max || 999));

      return {
        productId: String(control.dataset.productId || ''),
        variantId: String(option.value),
        title: String(control.dataset.productTitle || ''),
        variantTitle: String(option.dataset.variantTitle || ''),
        url: String(control.dataset.productUrl || ''),
        image: String(option.dataset.image || ''),
        price: Math.max(0, Number(option.dataset.price || 0)),
        max: maximum,
        quantity: Math.max(0, Math.min(maximum, Number(quantity || 0)))
      };
    }

    syncProduct(productId) {
      this.querySelectorAll('[data-pack-control]').forEach(control => {
        if (String(control.dataset.productId || '') !== productId) return;

        const selectedItem = this.selection.get(productId);
        const variantSelect = control.querySelector('[data-pack-variant-select]');
        const selectedOption = variantSelect && variantSelect.selectedOptions
          ? variantSelect.selectedOptions[0]
          : null;
        const currentVariantId = selectedOption ? String(selectedOption.value) : '';
        const quantity =
          selectedItem && String(selectedItem.variantId) === currentVariantId
            ? selectedItem.quantity
            : 0;
        const maximum = selectedOption ? Number(selectedOption.dataset.max || 999) : 0;

        const value = control.querySelector('[data-pack-quantity-value]');
        const decrease = control.querySelector('[data-pack-decrease]');
        const increase = control.querySelector('[data-pack-increase]');
        const productElement = control.closest('[data-pack-product]');

        if (value) value.textContent = String(quantity);
        if (decrease) decrease.disabled = quantity <= 0;
        if (increase) increase.disabled = maximum <= 0 || quantity >= maximum;
        if (productElement) productElement.classList.toggle('has-selection', quantity > 0);
      });
    }

    render() {
      const items = Array.from(this.selection.values()).filter(item => item.quantity > 0);
      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      const packTotal = items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );

      this.classList.toggle('has-selection', itemCount > 0);

      this.querySelectorAll('[data-pack-count]').forEach(element => {
        element.textContent = String(itemCount);
      });

      this.querySelectorAll('[data-pack-total]').forEach(element => {
        element.innerHTML = this.formatMoney(packTotal);
      });

      this.allPackElements('[data-pack-submit]').forEach(button => {
        button.disabled = itemCount === 0 || this.isAdding;
        button.classList.toggle('working', this.isAdding);
      });

      this.querySelectorAll('[data-pack-rewards]').forEach(notice => {
        notice.setAttribute(
          'data-cart-total-amount',
          String(this.baseCartTotal + packTotal)
        );
      });

      const itemsMarkup = items.length
        ? items.map(item => this.renderSummaryItem(item)).join('')
        : `<p class="mbp__empty text-size--small text-color--opacity">${this.escapeHtml(this.dataset.emptyLabel || '')}</p>`;

      this.querySelectorAll('[data-pack-items]').forEach(container => {
        container.innerHTML = itemsMarkup;
      });

      this.renderMobileRewards(packTotal, itemCount);
      if (this.mobileSticky) {
        const selected = this.mobileSticky.querySelector('[data-pack-mobile-selected]');
        if (selected) selected.innerHTML = itemsMarkup;
      }
    }

    renderSummaryItem(item) {
      const imageMarkup = item.image
        ? `<img src="${this.escapeHtml(item.image)}" alt="" width="80" height="80" loading="lazy">`
        : '<span class="mbp__summary-image-placeholder"></span>';
      const variantMarkup = item.variantTitle
        ? `<span>${this.escapeHtml(item.variantTitle)}</span>`
        : '';
      const removeLabel = this.dataset.removeLabel || 'Retirer';

      return `
        <div class="mbp__summary-item">
          <a class="mbp__summary-image" href="${this.escapeHtml(item.url)}" tabindex="-1">
            ${imageMarkup}
          </a>
          <div class="mbp__summary-item-text">
            <a class="mbp__summary-item-title text-size--small" href="${this.escapeHtml(item.url)}">
              ${this.escapeHtml(item.title)}
            </a>
            <span class="mbp__summary-item-meta text-size--xsmall">
              ${variantMarkup}
              <span>× ${item.quantity} · ${this.formatMoney(item.price * item.quantity)}</span>
            </span>
          </div>
          <button
            type="button"
            class="mbp__summary-remove"
            data-pack-remove="${this.escapeHtml(item.productId)}"
            aria-label="${this.escapeHtml(`${removeLabel} — ${item.title}`)}"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
            </svg>
          </button>
        </div>
      `;
    }

    async addPackToCart() {
      if (this.isAdding) return;

      const selectedItems = Array.from(this.selection.values()).filter(
        item => item.quantity > 0
      );
      if (!selectedItems.length) return;

      this.isAdding = true;
      this.setStatus('');
      this.render();

      const packTotal = selectedItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: selectedItems.map(item => ({
              id: item.variantId,
              quantity: item.quantity
            }))
          })
        });

        const result = await response.json();
        if (!response.ok || result.status === 422) {
          throw new Error(
            result.description || result.message || this.dataset.errorLabel || 'Le pack n’a pas pu être ajouté.'
          );
        }

        if (this.dataset.includeCartTotal === 'true') {
          this.baseCartTotal += packTotal;
        }

        this.selection.clear();
        this.persistSelection();
        this.querySelectorAll('[data-pack-control]').forEach(control => {
          this.syncProduct(String(control.dataset.productId || ''));
        });

        this.isAdding = false;
        this.render();
        this.allPackElements('[data-pack-status]').forEach(status => {
          status.textContent = this.dataset.successLabel || 'Votre pack a bien été ajouté au panier.';
        });

        const mobileSummary = this.querySelector('[data-pack-mobile-summary]');
        if (mobileSummary) mobileSummary.open = false;

        if (window.KROWN && KROWN.functions && typeof KROWN.functions.eventDispatcher === 'function') {
          KROWN.functions.eventDispatcher('krown:cart:add', {
            variantIds: selectedItems.map(item => item.variantId),
            source: 'composer-mon-pack-favrichon'
          });
        }

        const screenReaderInfo = document.getElementById('screen-reader-info');
        if (screenReaderInfo) {
          screenReaderInfo.textContent = this.dataset.successLabel || 'Votre pack a bien été ajouté au panier.';
          window.setTimeout(() => {
            screenReaderInfo.textContent = '';
          }, 1200);
        }

        if (typeof window.refreshCart === 'function') {
          window.refreshCart(true);
        } else {
          window.location.assign(this.dataset.cartUrl);
        }
      } catch (error) {
        this.isAdding = false;
        this.render();
        this.setStatus(error.message || this.dataset.errorLabel || 'Le pack n’a pas pu être ajouté.');
      }
    }

    restoreSelection() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.dataset.storageKey) || '[]');
        if (!Array.isArray(stored)) return;

        stored.forEach(item => {
          if (!item || !item.productId || !item.variantId || Number(item.quantity) <= 0) return;
          this.selection.set(String(item.productId), {
            productId: String(item.productId),
            variantId: String(item.variantId),
            title: String(item.title || ''),
            variantTitle: String(item.variantTitle || ''),
            url: String(item.url || ''),
            image: String(item.image || ''),
            price: Math.max(0, Number(item.price || 0)),
            max: Math.max(0, Number(item.max || 999)),
            quantity: Math.max(1, Number(item.quantity || 1))
          });
        });
      } catch (error) {
        sessionStorage.removeItem(this.dataset.storageKey);
      }
    }

    persistSelection() {
      try {
        sessionStorage.setItem(
          this.dataset.storageKey,
          JSON.stringify(Array.from(this.selection.values()))
        );
      } catch (error) {
        // La composition reste fonctionnelle même si le stockage est indisponible.
      }
    }

    setStatus(message) {
      this.allPackElements('[data-pack-status]').forEach(status => {
        status.textContent = message || '';
      });
    }

    formatMoney(cents) {
      const formatString = this.moneyFormat || '{{amount_with_comma_separator}} €';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

      const formatWithDelimiters = (number, precision, thousands, decimal) => {
        const safeNumber = Number(number);
        if (Number.isNaN(safeNumber)) return '0';

        const fixed = (safeNumber / 100).toFixed(precision);
        const parts = fixed.split('.');
        const amount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands || ','}`);
        const decimals = parts[1] ? `${decimal || '.'}${parts[1]}` : '';
        return amount + decimals;
      };

      const match = formatString.match(placeholderRegex);
      if (!match) return formatWithDelimiters(cents, 2, '.', ',');

      let value;
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2, ',', '.');
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2, '.', ',');
      }

      return formatString.replace(placeholderRegex, value);
    }

    escapeHtml(value) {
      return String(value || '').replace(/[&<>'"]/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#039;',
        '"': '&quot;'
      })[character]);
    }
  }

  customElements.define('pack-builder', PackBuilder);
}
